// ─── SignalR message hub client ────────────────────────────────────────────────
// Persistent connection to /hubs/messages. Fired once on login and torn down on
// logout. On every "MessageReceived" event, invalidates the react-query cache
// so the inbox drawer + bell + MessagesPage refresh immediately without a poll.

import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import type { QueryClient } from 'react-query';
import toast from 'react-hot-toast';
import logger from '../../utils/logger';
import { getRuntimeEnv } from '../../config/runtimeEnv';
import { MESSAGING_QUERY_KEY } from '../../api/messagingService';

// Same shape as the backend's RealtimeMessagePayload record. Kept as an interface
// (not a type import) so we don't couple the UI to Api DTO generation.
export interface RealtimeMessagePayload {
  messagePublicId: string;
  kind: string;
  from: string;
  subject?: string | null;
  body: string;
  sentAtUtc: string;
  threadPublicId?: string | null;
  senderUserPublicId?: string | null;
  relatedEntityType?: string | null;
  relatedEntityPublicId?: string | null;
}

let connection: HubConnection | null = null;

// The hub lives on the API host, one URL segment up from the versioned REST base
// (i.e. https://…/api/v1/… → https://…/hubs/messages). Strips the /api/vN suffix
// from the configured REST base so both stay in sync when the env var changes.
function resolveHubUrl(): string {
  const restBase = getRuntimeEnv('REACT_APP_API_BASE_URL', 'http://localhost:5025/api/v1');
  const stripped = restBase.replace(/\/api\/v\d+\/?$/, '');
  return `${stripped}/hubs/messages`;
}

export async function startMessageHub(qc: QueryClient): Promise<void> {
  if (connection && connection.state !== HubConnectionState.Disconnected) return;

  connection = new HubConnectionBuilder()
    .withUrl(resolveHubUrl(), {
      // Browser WebSocket can't set the Authorization header on the upgrade —
      // SignalR compensates by appending ?access_token=… when accessTokenFactory
      // is set. Backend accepts it only for /hubs/messages (see JwtAuthExtensions).
      accessTokenFactory: () => sessionStorage.getItem('auth_token') ?? '',
    })
    // Wait 2s → 5s → 10s → 30s on drop before giving up. Any missed event during
    // reconnect is caught by the 60s inbox refetchInterval fallback.
    .withAutomaticReconnect([2_000, 5_000, 10_000, 30_000])
    .configureLogging(LogLevel.Warning)
    .build();

  connection.on('MessageReceived', (payload: RealtimeMessagePayload) => {
    logger.debug('Realtime message received', payload);
    // Prefix invalidation on 'messaging' wipes inbox, unreadCount, threads, and every
    // threadMessages(id) cache in one call. Previously we invalidated each key
    // individually and also the specific thread by id — but if the payload's
    // threadPublicId ever mismatched the cached key (casing, missing field on some
    // event shapes), the open chat pane would keep rendering pre-arrival messages
    // until the driver refreshed. Nuking the whole namespace is the reliable path.
    qc.invalidateQueries(['messaging']);
    toast(`New message from ${payload.from}`, { icon: '📩' });
  });

  connection.onreconnected(() => {
    // On reconnect after a drop, refresh everything so nothing missed while offline
    // stays stale — including every open thread pane. Prefix invalidation on
    // 'messaging/thread' covers all threadMessages(id) caches in one call; without
    // it a chat opened before the drop keeps showing pre-drop messages.
    qc.invalidateQueries(MESSAGING_QUERY_KEY.inbox);
    qc.invalidateQueries(MESSAGING_QUERY_KEY.unreadCount);
    qc.invalidateQueries(MESSAGING_QUERY_KEY.threads);
    qc.invalidateQueries(['messaging', 'thread']);
  });

  try {
    await connection.start();
    logger.debug('Message hub connected');
  } catch (err) {
    logger.error('Message hub failed to start', err);
    connection = null;
  }
}

export async function stopMessageHub(): Promise<void> {
  if (!connection) return;
  try {
    await connection.stop();
  } catch (err) {
    logger.error('Message hub stop failed', err);
  }
  connection = null;
}

export function isMessageHubConnected(): boolean {
  return connection?.state === HubConnectionState.Connected;
}
